<script lang="ts">
  import type ArchieML from '$locales/en/content.json';

  interface Props {
    /* Whether the app is being rendered in an embed */
    embedded?: boolean;
    /* ArchieML story content */
    content: (typeof ArchieML)['story'];
  }

  let { embedded = false, content }: Props = $props();

  import {
    Article,
    Analytics,
    BodyText,
    EndNotes,
    SiteHeadline,
    GraphicBlock,
    InlineAd,
  } from '@reuters-graphics/graphics-components';
  import LogBlock from './components/dev/LogBlock.svelte';
  import { containerWidth, inlineAdNumber } from '$utils/propValidators';
  import { isReutersDotcom } from '$utils/env';
  import { page } from '$app/state';
  import pkg from '$pkg';

  // Line chart components
  import MultipleLineChart from './components/MultipleLineChart/index.svelte';
  import SmallMultiplesMultiLineCharts from './components/SmallMultiplesMultiLineCharts/index.svelte';
  import BarChart from './components/BarChart/index.svelte';
  // import LineChart from './components/LineChart/index.svelte';
  // import SmallMultiplesLineCharts from './components/SmallMultiplesLineCharts/index.svelte';

  // Import data from Google sheets
  import spData from '$locales/en/s&p-data.json';
  import vixData from '$locales/en/vix-data.json';
  import goldData from '$locales/en/presidency-gold.json';
  import usdData from '$locales/en/presidency-usd.json';
  import globalMktsData from '$locales/en/global-markets.json';
  import usGrowthData from '$locales/en/fed-us-gdp-growth.json';
  import bondsData from '$locales/en/presidency-bonds.json';

  // Props for line charts
  import {
    goldMultipleLineChartProps,
    spMultipleLineChartProps,
    globalMktSmallMultiplesProps,
    vixMultipleLineChartProps,
    usGdpGrowthBarChartProps,
    currencyMultipleLineChartProps,
    bondMultipleLineChartProps,
  } from './components/chartProps';

  // Util function to coerce the year values in data to the year 2025
  // This is so that the multi line chart can draw multiple lines over the same set of dates
  function formatDateAs2025<T extends Record<string, unknown>>(data: T[]) {
    return data.map((d) => {
      let dateVal = d.date as string;
      let dateParts = typeof dateVal === 'string' ? dateVal.split('/') : [];
      let newDate = `${dateParts[0]}/${dateParts[1]}/25`;
      return { ...d, date: newDate };
    });
  }
</script>

{#if !embedded && isReutersDotcom(page.url)}
  <Analytics authors={pkg?.reuters?.graphic?.authors || []} />
{/if}

<Article>
  <!--
    This component and others are part of our components library.
    📚 Read the docs: https://reuters-graphics.github.io/graphics-components/
  -->

  <SiteHeadline
    hed={content.hed}
    section={content.section}
    sectionUrl={content.sectionUrl}
    authors={content.authors}
    publishTime={content.publishedDate}
    updateTime={content.updatedDate}
  />

  <GraphicBlock
    width={containerWidth(content.width)}
    id={content.id}
    notes={content.notes}
    ariaDescription={content.altText}
  >
    <MultipleLineChart
      id={content.id}
      data={formatDateAs2025(spData.data)}
      {embedded}
      {...spMultipleLineChartProps}
    />
  </GraphicBlock>

  <!-- 🔁 Looping through your ArchieML doc blocks... -->
  {#each content.blocks as block}
    <!-- Text block -->
    {#if block.type === 'text'}
      <BodyText text={block.text} />

      <!-- Small multiples multi-line chart -->
    {:else if block.type === 'small-multiples-multi-line-charts' && block.id === 'global-mkt-by-president'}
      <SmallMultiplesMultiLineCharts
        data={globalMktsData.data}
        {...globalMktSmallMultiplesProps}
        id={block.id}
        width={containerWidth(block.width)}
        title={block.title}
        description={block.description}
        notes={block.notes}
        altText={block.altText}
      />

      <!-- Bar chart -->
    {:else if block.type === 'bar-chart'}
      <GraphicBlock
        width={containerWidth(block.width)}
        id={block.id}
        title={block.title}
        description={block.description}
        notes={block.notes}
        ariaDescription={block.altText}
      >
        <BarChart data={usGrowthData.data} {...usGdpGrowthBarChartProps} />
      </GraphicBlock>

      <!-- Single line chart -->
      <!-- {:else if block.type === 'line-chart' && block.id === 'id-for-your-chart'}
      <GraphicBlock
        width={containerWidth(block.width)}
        id={block.id}
        title={block.title}
        description={block.description}
        notes={block.notes}
        ariaDescription={block.altText}
      >
        <LineChart
          data={vixData.data}
          xValKey="add your x value key here"
          yValKey="add your y value key here"
          dateParser="%d/%m/%y"
          dateFormatter="%b. %-d"
        />
      </GraphicBlock> -->

      <!-- Multiple line chart -->
    {:else if block.type === 'multiple-line-chart' && block.id === 'presidency-vix'}
      <GraphicBlock
        width={containerWidth(block.width)}
        id={block.id}
        title={block.title}
        description={block.description}
        notes={block.notes}
        ariaDescription={block.altText}
      >
        <MultipleLineChart
          id={block.id}
          data={formatDateAs2025(vixData.data)}
          {embedded}
          {...vixMultipleLineChartProps}
        />
      </GraphicBlock>
    {:else if block.type === 'multiple-line-chart' && block.id === 'presidency-gold'}
      <GraphicBlock
        width={containerWidth(block.width)}
        id={block.id}
        title={block.title}
        description={block.description}
        notes={block.notes}
        ariaDescription={block.altText}
      >
        <MultipleLineChart
          data={formatDateAs2025(goldData.data)}
          {...goldMultipleLineChartProps}
          {embedded}
        />
      </GraphicBlock>
    {:else if block.type === 'multiple-line-chart' && block.id === 'presidency-usd'}
      <GraphicBlock
        width={containerWidth(block.width)}
        id={block.id}
        title={block.title}
        description={block.description}
        notes={block.notes}
        ariaDescription={block.altText}
      >
        <MultipleLineChart
          data={formatDateAs2025(usdData.data)}
          {...currencyMultipleLineChartProps}
          {embedded}
        />
      </GraphicBlock>
    {:else if block.type === 'multiple-line-chart' && block.id === 'presidency-bonds'}
      <GraphicBlock
        width={containerWidth(block.width)}
        id={block.id}
        title={block.title}
        description={block.description}
        notes={block.notes}
        ariaDescription={block.altText}
      >
        <MultipleLineChart
          data={formatDateAs2025(bondsData.data)}
          {...bondMultipleLineChartProps}
        />
      </GraphicBlock>

      <!-- Inline ad -->
    {:else if block.type === 'inline-ad'}
      {#if isReutersDotcom(page.url)}
        <InlineAd n={inlineAdNumber(block.n)} />
      {:else}
        <LogBlock level="info" message="An ad will appear here on dotcom" />
      {/if}

      <!-- Warning block -->
    {:else}
      <LogBlock message={`Unknown block type: "${block.type}"`} />
    {/if}
  {/each}

  <EndNotes notes={content.endNotes} />
</Article>
